import { cloneDeep, isEmpty, isEqual, pick } from 'lodash';
import { Types } from 'mongoose';
import {
  createInsertionOrder,
  editInsertionOrder,
  type InsertionOrder,
  InsertionOrderEntity,
  type StoreWithEntity,
  setWithEntity,
  type UserEntity
} from 'sonata-static-storage/lib/entities';
import { isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { create } from 'zustand';
import createContext from 'zustand/context';

import { countAdChanges } from '@/helpers/ads/adSaveActions.helper';
import {
  EMPTY_DOOH_AUDIENCE,
  hasDoohAudienceChanges,
  persistDoohAudience
} from '@/helpers/doohAudience/doohAudience.helper';
import {
  CONTEXT_TARGETING_FIELDS,
  CREATIVES_OTHER_FIELDS,
  countChangedFields,
  MAIN_SETTINGS_EXCLUDED_FIELDS
} from '@/helpers/entity/entityFieldChanges.helper';
import { withFlightColors } from '@/helpers/ioSettings/flightColors.helper';
import { hasRetargetingSegmentToSync } from '@/helpers/targeting/retargetingSegment.helper';
import { EMPTY_SAVE_STEP_COUNTS } from '@/types/saveSteps.types';

import type { DeepPartial } from 'sonata-static-storage/lib/common/common.types';
import type { AdInterface } from 'sonata-static-storage/lib/schemas/ad/ad.schema';
import type { AttributionSettings } from 'sonata-static-storage/lib/schemas/campaign/attributionSettings.schema';
import type { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import type { ContextualBiddingInterface } from 'sonata-static-storage/lib/schemas/campaign/contextualBidding.schema';
import type { OptimizationModelInterface } from 'sonata-static-storage/lib/schemas/campaign/optimizationModel.schema';
import type { PricingModelConfig } from 'sonata-static-storage/lib/schemas/campaign/pricingModel.schema';
import type { SchedulePartingInterface } from 'sonata-static-storage/lib/schemas/campaign/scheduleParting.schema';
import type { ScreenGroupInterface } from 'sonata-static-storage/lib/schemas/campaign/screenGroup.schema';
import type { ZoneGroupInterface } from 'sonata-static-storage/lib/schemas/campaign/zoneGroup.schema';
import type { CampaignPlan } from 'sonata-static-storage/lib/schemas/multiFlight/campaignPlan.schema';
import type { FrequencyCapping } from 'sonata-static-storage/lib/schemas/multiFlight/frequencyCapping.schema';
import type { InsertionOrderFlight } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrderFlight.schema';
import type { PixelAssociatedEntity } from 'sonata-static-storage/lib/schemas/pixel/pixel.schema';
import type {
  DoohTargetingBaseType,
  TargetingBaseType
} from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';
import type { DoohAudience } from '@/services/doohAudience.service';
import type { SaveStepCounts, SaveStepStatus, SaveSteps } from '@/types/saveSteps.types';

export type IoSettingsStore = StoreWithEntity<
  Omit<InsertionOrder, 'type'>,
  InsertionOrderEntity,
  {
    savedBaseline: Record<string, unknown>;
    contextTargetingCount: number;
    creativesOtherCount: number;
    doohAudience: DoohAudience;
    editIoSettings: (requestConfig?: Omit<RequestInit, 'data' | 'method'>) => Promise<{ _id?: string }>;
    errors: string[] | null;
    isLoading: boolean;
    hasUnsavedMapChanges: boolean;
    mainSettingsCount: number;
    mapSave: () => Promise<void>;
    mapSavePromise: Promise<void> | null;
    mediaType: InsertionOrder['mediaType'];
    pendingToCreate?: boolean;
    pixels: InsertionOrder['pixels'];
    resetMapSave: () => void;
    resolveMapSave: (() => void) | null;
    saveIoSettings: (requestConfig?: Omit<RequestInit, 'data' | 'method'>) => Promise<{ _id?: string } | void>;
    savedDoohAudience: DoohAudience;
    pendingSaveSteps: Pick<SaveSteps, 'venues' | 'attributionZones' | 'screens'>;
    saveSteps: SaveSteps;
    setAds: (adsData: InsertionOrder['ads']) => void;
    setAdsDistribution: (newState: InsertionOrder['adsDistribution']) => void;
    setAdsPriceEnabled: (newState: InsertionOrder['adsPriceEnabled']) => void;
    setAttributionSettings: (attributionSettingsData: AttributionSettings) => void;
    setCompleted: (completedState: boolean) => void;
    setContextualBidding: (contextualBidding: ContextualBiddingInterface) => void;
    setCountries: (countries: string[]) => void;
    setCustomEvents: (newState: InsertionOrder['customEvents']) => void;
    setDco: (dcoData: InsertionOrder['dco']) => void;
    setDoohAudience: (doohAudience: DoohAudience) => void;
    setFlightSettings: (flightSettingsData: InsertionOrderFlight[]) => void;
    setIoName: (ioNameData: string) => void;
    setIoNumber: (ioNumberData: string) => void;
    setHasUnsavedMapChanges: (hasUnsavedChanges: boolean) => void;
    setMediaChannelSelected: (
      mediaChannel: CampaignFamilyEnum,
      extraArgs?: {
        user: UserInterface<string>;
      }
    ) => void;
    setMediaType: (mediaType: InsertionOrder['mediaType']) => void;
    setOptimizationModel: (optimizationModel?: OptimizationModelInterface) => void;
    setPixels: (pixels: PixelAssociatedEntity[]) => void;
    setPricingModelConfig: (pricingModelConfig: PricingModelConfig) => void;
    setSaveStep: (step: keyof SaveSteps, status: SaveStepStatus, counts?: SaveStepCounts) => void;
    setPendingSaveSteps: (steps: Pick<SaveSteps, 'venues' | 'attributionZones' | 'screens'>) => void;
    setScheduleParting: (schedulePartingData: SchedulePartingInterface) => void;
    setScreenGroups: (screenGroups: ScreenGroupInterface[]) => void;
    setTargeting: (targeting?: TargetingBaseType | DoohTargetingBaseType) => void;
    setUserFrequencyCapping: (FrequencyCapping: FrequencyCapping) => void;
    setVenues: (venues: Venue[]) => void;
    setZoneGroups: (zoneGroups: ZoneGroupInterface<string>[]) => void;
    shouldMapSave: boolean;
    targeting?: TargetingBaseType;
    type: CampaignFamilyEnum | undefined;
    venues: Venue<string>[];
    zoneGroups: ZoneGroupInterface<string>[];
  }
>;

type CreateStoreOpts = {
  doohAudience?: DoohAudience;
};

const { Provider, useStore } = createContext();

const TRACKED_FIELDS = [
  '_id',
  'ads',
  'adsDistribution',
  'adsPriceEnabled',
  'attributionSettings',
  'completed',
  'contextualBidding',
  'countries',
  'customEvents',
  'dco',
  'flights',
  'ioNumber',
  'mediaType',
  'name',
  'optimizationModel',
  'pixels',
  'pricingModelConfig',
  'scheduleParting',
  'screenGroups',
  'targeting',
  'type',
  'userFrequencyCapping',
  'zoneGroups'
] as const;

const MAIN_SETTINGS_FIELDS = TRACKED_FIELDS.filter(
  field => !(MAIN_SETTINGS_EXCLUDED_FIELDS as readonly string[]).includes(field)
);

const getHasUnsavedEntityChanges = (store: IoSettingsStore): boolean =>
  !isEqual(pick(store, TRACKED_FIELDS), store.savedBaseline);

const getHasUnsavedDoohAudienceChanges = (store: IoSettingsStore): boolean =>
  hasDoohAudienceChanges(store.doohAudience, store.savedDoohAudience);

const getHasUnsavedChanges = (store: IoSettingsStore): boolean =>
  getHasUnsavedEntityChanges(store) || getHasUnsavedDoohAudienceChanges(store) || store.hasUnsavedMapChanges;

const initialState = (io?: DeepPartial<InsertionOrder>) => ({
  ...InsertionOrderEntity.baseDefaultValues,
  ...io,
  // MTF-864: flights saved before `color` existed get a palette colour (in the baseline too); the next save persists it
  ...(io?.flights && { flights: withFlightColors(io.flights) }),
  _id: io?._id || new Types.ObjectId().toString(),
  errors: null,
  isLoading: false,
  pendingToCreate: !io?._id
});

const createStore = (
  io: DeepPartial<InsertionOrder> | undefined,
  cp: CampaignPlan,
  user: UserEntity,
  { doohAudience = EMPTY_DOOH_AUDIENCE }: CreateStoreOpts = {}
) =>
  create<IoSettingsStore>(
    // @ts-expect-error
    (set, get) => {
      const isANewIO = isEmpty(io);
      const campaignPlanId = io?.campaignPlan || cp._id;
      const entity = new InsertionOrderEntity(initialState(io), user, isANewIO);
      // The stored audience replaces the one of the form: with a text, it holds the variables resolved with AI
      const saveDoohAudience = async (insertionOrderId: string) => {
        const { doohAudience: current, savedDoohAudience: saved, type } = get();
        const stored = await persistDoohAudience({
          current,
          entity: 'insertionOrder',
          entityId: insertionOrderId,
          isDooh: Boolean(type && isDOOHCampaign(type)),
          saved,
          userId: user.get('id')!
        });
        if (stored) {
          set({ doohAudience: stored, savedDoohAudience: stored });
        }
      };

      return {
        ...entity.toJSON(),
        campaignPlan: campaignPlanId,
        contextTargetingCount: 0,
        creativesOtherCount: 0,
        doohAudience,
        editIoSettings: async (requestConfig?: Omit<RequestInit, 'method' | 'data'>) => {
          set({ isLoading: true });
          const hasUnsavedEntityChanges = getHasUnsavedEntityChanges(get());
          const hasEntityModelChanges = hasUnsavedEntityChanges || getHasUnsavedDoohAudienceChanges(get());
          const savedBaseline = get().savedBaseline as Record<string, unknown>;
          const creativesCounts = countAdChanges(
            get().ads,
            (savedBaseline as { ads?: AdInterface<string>[] }).ads ?? []
          );
          const mainSettingsCount = countChangedFields(get(), savedBaseline, MAIN_SETTINGS_FIELDS);
          const contextTargetingCount = countChangedFields(get(), savedBaseline, CONTEXT_TARGETING_FIELDS);
          const creativesOtherCount = countChangedFields(get(), savedBaseline, CREATIVES_OTHER_FIELDS);
          // The map provider owns the venues and screens steps: it reports them
          // (with their counts) as soon as `mapSave` reaches it.
          set({
            contextTargetingCount,
            creativesOtherCount,
            mainSettingsCount,
            saveSteps: {
              attributionZones: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
              entityModel: { counts: creativesCounts, status: hasEntityModelChanges ? 'pending' : 'disabled' },
              screens: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
              venues: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' }
            }
          });
          await get().mapSave();
          const mapSteps = get().saveSteps;
          if (
            mapSteps.venues.status === 'fail' ||
            mapSteps.attributionZones.status === 'fail' ||
            mapSteps.screens.status === 'fail'
          ) {
            set({ isLoading: false });
            throw new Error('Map data failed to save, aborting entity update');
          }
          const currentState = get();
          const {
            _id,
            ads,
            adsDistribution,
            adsPriceEnabled,
            attributionSettings,
            completed,
            contextualBidding,
            countries,
            customEvents,
            dco,
            flights,
            ioNumber,
            mediaType,
            name,
            optimizationModel,
            pixels,
            pricingModelConfig,
            scheduleParting,
            screenGroups,
            targeting,
            type,
            userFrequencyCapping,
            zoneGroups
          } = currentState;
          let updatedInsertionOrder;
          if (hasEntityModelChanges) {
            get().setSaveStep('entityModel', 'processing');
          }
          try {
            updatedInsertionOrder = await editInsertionOrder(
              user.get('id')!,
              io?._id || _id!,
              {
                ads,
                adsDistribution,
                adsPriceEnabled,
                attributionSettings,
                campaignPlan: campaignPlanId,
                completed,
                contextualBidding,
                countries,
                customEvents,
                dco,
                flights,
                ioNumber,
                mediaType,
                name,
                optimizationModel,
                pixels,
                pricingModelConfig,
                scheduleParting,
                screenGroups,
                targeting,
                type,
                userFrequencyCapping,
                zoneGroups
              },
              requestConfig
            );
            if (hasRetargetingSegmentToSync(targeting)) {
              get().setTargeting((updatedInsertionOrder as InsertionOrder).targeting);
            }
            // Only advance the baseline on a real success — resetting it on
            // failure too would make the still-unsaved changes look saved.
            set({ savedBaseline: cloneDeep(pick(get(), TRACKED_FIELDS)) });
            await saveDoohAudience(io?._id || _id!);
            if (hasEntityModelChanges) {
              get().setSaveStep('entityModel', 'success');
            }
          } catch (error) {
            get().setSaveStep('entityModel', 'fail');
            throw error;
          } finally {
            set({ isLoading: false });
          }
          return updatedInsertionOrder as { _id?: string };
        },
        entity,
        hasUnsavedMapChanges: false,
        mainSettingsCount: 0,
        mapSave: () => {
          const existing = get().mapSavePromise;
          if (existing) {
            return existing;
          }
          let resolver!: () => void;
          const promise = new Promise<void>(resolve => {
            resolver = resolve;
          });
          set({ mapSavePromise: promise, resolveMapSave: resolver, shouldMapSave: true });
          return promise;
        },
        mapSavePromise: null,
        pendingSaveSteps: {
          attributionZones: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'disabled' },
          screens: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'disabled' },
          venues: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'disabled' }
        },
        resetMapSave: () => {
          const { resolveMapSave } = get();
          resolveMapSave?.();
          set({ mapSavePromise: null, resolveMapSave: null, shouldMapSave: false });
        },
        resolveMapSave: null,
        saveIoSettings: async (requestConfig?: Omit<RequestInit, 'method' | 'data'>) => {
          const currentState = get();
          if (currentState.pendingToCreate) {
            set({ isLoading: true });
            const savedBaseline = currentState.savedBaseline as Record<string, unknown>;
            const creativesCounts = countAdChanges(
              currentState.ads,
              (savedBaseline as { ads?: AdInterface<string>[] }).ads ?? []
            );
            const mainSettingsCount = countChangedFields(currentState, savedBaseline, MAIN_SETTINGS_FIELDS);
            const contextTargetingCount = countChangedFields(currentState, savedBaseline, CONTEXT_TARGETING_FIELDS);
            const creativesOtherCount = countChangedFields(currentState, savedBaseline, CREATIVES_OTHER_FIELDS);
            // Creating from scratch: the entity always saves, and the map
            // provider reports its own two steps once `mapSave` runs below.
            set({
              contextTargetingCount,
              creativesOtherCount,
              mainSettingsCount,
              saveSteps: {
                attributionZones: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
                entityModel: { counts: creativesCounts, status: 'pending' },
                screens: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
                venues: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' }
              }
            });
            const {
              _id,
              ads,
              adsDistribution,
              adsPriceEnabled,
              attributionSettings,
              completed,
              contextualBidding,
              countries,
              customEvents,
              dco,
              flights,
              ioNumber,
              mediaType,
              name,
              optimizationModel,
              pixels,
              pricingModelConfig,
              scheduleParting,
              screenGroups,
              targeting,
              type,
              userFrequencyCapping,
              zoneGroups
            } = currentState;
            let newIO: InsertionOrderEntity;
            get().setSaveStep('entityModel', 'processing');
            try {
              newIO = await createInsertionOrder(
                user.get('id')!,
                {
                  _id,
                  ads,
                  adsDistribution,
                  adsPriceEnabled,
                  attributionSettings,
                  campaignPlan: campaignPlanId,
                  completed,
                  contextualBidding,
                  countries,
                  customEvents,
                  dco,
                  flights,
                  ioNumber,
                  mediaType,
                  name,
                  optimizationModel,
                  pixels,
                  pricingModelConfig,
                  scheduleParting,
                  screenGroups,
                  targeting,
                  type,
                  userFrequencyCapping,
                  zoneGroups
                },
                requestConfig
              );
              get().setSaveStep('entityModel', 'success');
            } catch (error) {
              set({ isLoading: false });
              get().setSaveStep('entityModel', 'fail');
              throw error;
            }
            if (hasRetargetingSegmentToSync(targeting)) {
              get().setTargeting(newIO.entity.targeting);
            }
            // Mutate the existing entity in place rather than replacing it —
            // setAds/setAdsDistribution/etc. were bound to this entity instance
            // at store-creation time, so swapping in a new instance here would
            // silently detach them from what the UI renders.
            set(state => {
              state.entity.set('_id', newIO.entity._id);
              return {
                entity: state.entity,
                isLoading: false,
                pendingToCreate: false
              };
            });
            set({ savedBaseline: cloneDeep(pick(get(), TRACKED_FIELDS)) });
            // A new insertion order has to exist before its audience can be saved
            try {
              await saveDoohAudience(newIO.entity._id || _id!);
            } catch (error) {
              get().setSaveStep('entityModel', 'fail');
              throw error;
            }
            if (newIO.entity._id) {
              await get().mapSave();
            }
            return newIO.entity as { _id?: string };
          }
        },
        saveSteps: {
          attributionZones: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
          entityModel: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
          screens: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' },
          venues: { counts: EMPTY_SAVE_STEP_COUNTS, status: 'pending' }
        },
        savedBaseline: cloneDeep(pick(entity.toJSON(), TRACKED_FIELDS)),
        savedDoohAudience: doohAudience,
        setAds: setWithEntity<InsertionOrderEntity>('ads', entity, set),
        setAdsDistribution: setWithEntity<InsertionOrderEntity>('adsDistribution', entity, set),
        setAdsPriceEnabled: setWithEntity<InsertionOrderEntity>('adsPriceEnabled', entity, set),
        setAttributionSettings: setWithEntity<InsertionOrderEntity>('attributionSettings', entity, set),
        setCompleted: setWithEntity<InsertionOrderEntity>('completed', entity, set),
        setContextualBidding: setWithEntity<InsertionOrderEntity>('contextualBidding', entity, set),
        setCountries: setWithEntity<InsertionOrderEntity>('countries', entity, set),
        setCustomEvents: setWithEntity<InsertionOrderEntity>('customEvents', entity, set),
        setDco: setWithEntity<InsertionOrderEntity>('dco', entity, set),
        setDoohAudience: (newDoohAudience: DoohAudience) => set({ doohAudience: newDoohAudience }),
        setFlightSettings: setWithEntity<InsertionOrderEntity>('flights', entity, set),
        setHasUnsavedMapChanges: (hasUnsavedMapChanges: boolean) => set({ hasUnsavedMapChanges }),
        setIoName: setWithEntity<InsertionOrderEntity>('name', entity, set),
        setIoNumber: setWithEntity<InsertionOrderEntity>('ioNumber', entity, set),
        setMediaChannelSelected: setWithEntity<InsertionOrderEntity, 'type', { user: UserInterface<string> }>(
          'type',
          entity,
          set
        ),
        setMediaType: setWithEntity<InsertionOrderEntity>('mediaType', entity, set),
        setOptimizationModel: setWithEntity<InsertionOrderEntity>('optimizationModel', entity, set),
        setPendingSaveSteps: pendingSaveSteps => set({ pendingSaveSteps }),
        setPixels: setWithEntity<InsertionOrderEntity>('pixels', entity, set),
        setPricingModelConfig: setWithEntity<InsertionOrderEntity>('pricingModelConfig', entity, set),
        setSaveStep: (step, status, counts) =>
          set(state => ({
            saveSteps: { ...state.saveSteps, [step]: { counts: counts ?? state.saveSteps[step].counts, status } }
          })),
        setScheduleParting: setWithEntity<InsertionOrderEntity>('scheduleParting', entity, set),
        setScreenGroups: setWithEntity<InsertionOrderEntity, 'screenGroups'>('screenGroups', entity, set),
        setTargeting: setWithEntity<InsertionOrderEntity>('targeting', entity, set),
        setUserFrequencyCapping: setWithEntity<InsertionOrderEntity>('userFrequencyCapping', entity, set),
        setVenues: (venues: Venue<string>[]) => {
          set({ venues });
          const ads = get().ads;
          const adsWithFilteredVenues = ads.map((ad: AdInterface<string>) => ({
            ...ad,
            adOptions: {
              ...ad.adOptions,
              // Avoid having ads with venues that have been removed of the IO
              shops:
                ad.adOptions?.shops?.filter(v =>
                  venues.some(venue => venue._id?.toString() === (v as unknown as string))
                ) || []
            }
          }));
          set({ ads: adsWithFilteredVenues as AdInterface<string>[] });
        },
        setZoneGroups: setWithEntity<InsertionOrderEntity, 'zoneGroups'>('zoneGroups', entity, set),
        shouldMapSave: false,
        user: user.get('id'),
        venues: []
      };
    }
  );

export {
  createStore,
  getHasUnsavedChanges,
  getHasUnsavedDoohAudienceChanges,
  getHasUnsavedEntityChanges,
  MAIN_SETTINGS_FIELDS,
  Provider,
  useStore
};
